import { useCallback, useEffect, useRef, useState } from 'react';
// VoicePhase exported below for the UI's status badge.
import { supabase } from './supabase';

interface TranscribeResponse {
  text?: unknown;
  error?: unknown;
}

interface UseVoiceToTextOptions {
  onTranscript: (text: string) => void;
}

/** Post-process a raw STT transcript to fix common Chrome/Google ASR
 *  mishearings of soccer-specific vocabulary. The recogniser has no
 *  language-model bias toward soccer terms, so "U11" arrives as "you 11"
 *  and "Owls" as "hours" / "ours". Applied to the FINAL transcript only;
 *  the live interim panel shows the raw text so the coach can see what
 *  was heard, then the normaliser cleans it before Brain ingests it. */
export function normaliseSoccerTranscript(raw: string): string {
  let out = raw;
  // Age-group bands. "you 11" / "u 11" / "u-11" → "U11". Number range 5–23
  // covers every youth-soccer age group plus a buffer.
  out = out.replace(/\b(?:you|u|under)\s*-?\s*(\d{1,2})\b/gi, (_m, n) => {
    const num = Number(n);
    return num >= 5 && num <= 23 ? `U${num}` : _m;
  });
  // Common homophones for soccer terminology. Order matters — narrower first.
  const subs: Array<[RegExp, string]> = [
    // ── Non-league rescues. STT often mangles "non-league" badly:
    //   "non-league"   → "monthly", "non leg", "non lake"
    //   "played non-league level" → "play level level monthly"
    // Scoped patterns first (specific multi-word phrases), then the generic
    // canonicalisation of any "non league" / "non-leg" → "non-league".
    [/\bplay(?:ed|ing)?\s+level\s+(?:level\s+)?monthly\b/gi, 'played non-league'],
    [/\bplay(?:ed|ing)?\s+monthly\b/gi, 'played non-league'],
    [/\bnon\s*[- ]?\s*leg\b/gi, 'non-league'],
    [/\bnon\s*[- ]?\s*lake\b/gi, 'non-league'],
    [/\bnon\s*[- ]?\s*league\b/gi, 'non-league'],
    [/\bno\s+league\b/gi, 'non-league'],

    [/\b(?:hours|ours)\b/gi, 'Owls'],
    [/\beagle one(?:s)?\b/gi, 'Eagles'],
    [/\b(\d{1,2})\s*-?\s*aside\b/gi, '$1-a-side'],
    [/\b(?:four corner|4 corner)\s*(?:model|approach)?\b/gi, '4 Corner Model'],
    [/\bback for\b/gi, 'back four'],
    [/\bfor four two\b/gi, '4-4-2'],
    [/\bfor three three\b/gi, '4-3-3'],
    [/\bfor two three one\b/gi, '4-2-3-1'],
    [/\bthree five two\b/gi, '3-5-2'],
    // UK football vernacular STT rescues. Web Speech often murders these:
    //   "tiki-taka"     → "tickey tacker" / "ticky tack"
    //   "gegenpressing" → "gay gem pressing" / "Egan pressing"
    //   "the gaffer"    → "the gap her" (sometimes)
    //   "5-a-side"      → already canonicalised above
    //   "Astro"         → "as throw" (rare)
    //   "tackle"        → usually fine
    //   "9v9" / "11v11" → "nine v nine" / "eleven v eleven"
    [/\btickey?\s*tacker?\b/gi, 'tiki-taka'],
    [/\btick(?:y|ey)\s+tack\b/gi, 'tiki-taka'],
    [/\bgay\s*gem\s*pressing\b/gi, 'gegenpressing'],
    [/\began\s+pressing\b/gi, 'gegenpressing'],
    [/\bthe\s+gap\s+her\b/gi, 'the gaffer'],
    [/\bas\s+throw\b/gi, 'AstroTurf'],
    [/\bnine\s*v\s*nine\b/gi, '9v9'],
    [/\beleven\s*v\s*eleven\b/gi, '11v11'],
    [/\bseven\s*v\s*seven\b/gi, '7v7'],
    [/\bfive\s*v\s*five\b/gi, '5v5'],
    [/\bhigh\s*line\s+press\b/gi, 'high press'],
    [/\bmid\s*block\b/gi, 'mid-block'],
    [/\blow\s*block\b/gi, 'low block'],
    [/\bhalf\s*spaces?\b/gi, 'half-space'],
    [/\bhalf\s*turn\b/gi, 'half-turn'],
    [/\bthird\s*man\s*run\b/gi, 'third-man run'],
    [/\broute\s+one\b/gi, 'Route One'],
    // Common UK coaching-phrase mishearings. STT often murders these:
    //   "see [the/my] players develop" → "same place develop"
    //   "see them develop"             → "same them develop"
    //   "watching them develop"        → "watch then develop"
    [/\bsame\s+place\s+develop\b/gi, 'see players develop'],
    [/\bsame\s+them\s+develop\b/gi, 'see them develop'],
    [/\bwatch\s+then\s+develop\b/gi, 'watching them develop'],
    [/\bcome\s+together\s+as\s+a\s+team\b/gi, 'come together as a team'],
    [/\bgrow\s+as\s+a\s+team\b/gi, 'grow as a team'],
    // "non-league" further variants
    [/\bthe\s+bed\b/gi, 'non-league'],
    [/\bnone\s+league\b/gi, 'non-league'],
  ];
  for (const [re, rep] of subs) {
    out = out.replace(re, rep);
  }
  // Collapse whitespace.
  return out.replace(/\s+/g, ' ').trim();
}

/** Distilled state for the UI to colour-code a status badge. */
export type VoicePhase =
  | 'idle'              // not started
  | 'requesting'        // asked for mic permission / recogniser init
  | 'armed'             // recogniser running but no audio detected yet
  | 'listening'         // audio arriving, no recognised words yet
  | 'hearing'           // words being recognised
  | 'transcribing'      // recorder stopped, awaiting Whisper response
  | 'ended'             // session ended normally
  | 'error'             // permission denied, network, etc.
  | 'unsupported';      // browser lacks Web Speech AND has no upload path

const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
];

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return MIME_CANDIDATES.find((mime) => MediaRecorder.isTypeSupported(mime));
}

function extensionForMime(mimeType: string): string {
  if (mimeType.includes('mp4')) return 'm4a';
  if (mimeType.includes('webm')) return 'webm';
  return 'webm';
}

/**
 * Server-side transcription via the Supabase edge function. Used as
 * a fallback when the browser doesn't expose the Web Speech API
 * (Firefox, older Safari).
 */
async function transcribeAudio(blob: Blob): Promise<string> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!supabaseUrl || !anonKey) {
    throw new Error('Supabase function client is not configured');
  }

  const functionsUrl = `${supabaseUrl.replace(/\/$/, '')}/functions/v1`;
  const { data } = await supabase.auth.getSession();
  const accessToken = data.session?.access_token ?? anonKey;
  const form = new FormData();
  const extension = extensionForMime(blob.type);
  form.set('file', new File([blob], `voice.${extension}`, { type: blob.type || 'audio/webm' }));

  const response = await fetch(`${functionsUrl}/transcribe`, {
    method: 'POST',
    headers: {
      'apikey': anonKey,
      'Authorization': `Bearer ${accessToken}`,
    },
    body: form,
  });

  const payload = await response.json() as TranscribeResponse;
  if (!response.ok) {
    throw new Error(typeof payload.error === 'string' ? payload.error : `transcribe failed (${response.status})`);
  }
  if (typeof payload.text !== 'string' || !payload.text.trim()) {
    throw new Error('transcribe returned no text');
  }
  return payload.text.trim();
}

// ---------------------------------------------------------------------------
// Browser-native Web Speech API — when available, recognition runs entirely
// in the browser (Chrome / Edge route it through Google's cloud STT). No
// backend dependency, so offline-stub builds get a working mic too.
// ---------------------------------------------------------------------------

interface SpeechRecognitionEventAlt extends Event {
  results: ArrayLike<{ 0: { transcript: string }; isFinal: boolean }> & { length: number };
}
interface SpeechRecognitionErrorEventAlt extends Event {
  error: string;
  message?: string;
}
interface SpeechRecognitionAlt extends EventTarget {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEventAlt) => void) | null;
  onerror: ((e: SpeechRecognitionErrorEventAlt) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
  onaudiostart: (() => void) | null;
  onspeechstart: (() => void) | null;
  onaudioend: (() => void) | null;
  onspeechend: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionAlt;

function getNativeSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function useVoiceToText({ onTranscript }: UseVoiceToTextOptions) {
  const [listening, setListening] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  // Live partial transcript — updated continuously while the user speaks
  // so the UI can show the words as they're recognised. Cleared whenever
  // the listen session ends (onend / stop / error).
  const [interim, setInterim] = useState('');
  // Distilled lifecycle state for the UI's status badge.
  const [phase, setPhase] = useState<VoicePhase>('idle');
  // Mic permission state from the Permissions API (best-effort — Firefox
  // doesn't support querying 'microphone'). Surfaced so the UI can flag
  // a denied permission before the coach even clicks the mic.
  const [permission, setPermission] = useState<'granted' | 'denied' | 'prompt' | 'unknown'>('unknown');

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return;
    let cancelled = false;
    // Cast — Permissions.query accepts 'microphone' on Chromium / Safari but
    // its TS lib types don't list it.
    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then((result) => {
        if (cancelled) return;
        setPermission(result.state as 'granted' | 'denied' | 'prompt');
        result.onchange = () => {
          if (!cancelled) setPermission(result.state as 'granted' | 'denied' | 'prompt');
        };
      })
      .catch(() => {
        if (!cancelled) setPermission('unknown');
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const busyRef = useRef(false);

  // Native Web Speech API recogniser (when available). Initialised lazily
  // on the first start() call. Kept in a ref so we can abort on stop().
  const recognitionRef = useRef<SpeechRecognitionAlt | null>(null);
  const nativeCtor = getNativeSpeechRecognitionCtor();
  // Final transcript accumulator — multiple onresult events fire for one
  // utterance (interim → final); we concatenate the finalised pieces.
  const finalTranscriptRef = useRef('');
  // Silence auto-stop — two parallel signals to make this snappy.
  //
  // Signal A: onspeechend fires after the BROWSER's VAD confirms silence.
  //   Reliable but Chrome's internal threshold is 1-2s on top of the
  //   actual pause — so the perceived gap was 3-5s. We keep this as a
  //   backup but it's the slow path.
  //
  // Signal B: onresult fires with isFinal=true AND no further interim
  //   activity for FINAL_RESULT_HOLD_MS. This is the FAST path — final
  //   results typically arrive within ~200ms of the coach stopping
  //   speaking. Cancelled if more interim content comes in.
  //
  // Whichever fires first stops the recogniser → onend → onTranscript.
  // onspeechstart cancels both timers so mid-thought pauses recover.
  const SPEECHEND_HOLD_MS = 300;
  const FINAL_RESULT_HOLD_MS = 250;
  const silenceTimerRef = useRef<number | null>(null);
  const finalResultTimerRef = useRef<number | null>(null);
  const armSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current !== null) window.clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = window.setTimeout(() => {
      silenceTimerRef.current = null;
      try { recognitionRef.current?.stop(); } catch { /* noop */ }
    }, SPEECHEND_HOLD_MS);
  }, []);
  const armFinalResultTimer = useCallback(() => {
    if (finalResultTimerRef.current !== null) window.clearTimeout(finalResultTimerRef.current);
    finalResultTimerRef.current = window.setTimeout(() => {
      finalResultTimerRef.current = null;
      try { recognitionRef.current?.stop(); } catch { /* noop */ }
    }, FINAL_RESULT_HOLD_MS);
  }, []);
  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current !== null) {
      window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (finalResultTimerRef.current !== null) {
      window.clearTimeout(finalResultTimerRef.current);
      finalResultTimerRef.current = null;
    }
  }, []);

  const cleanup = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
    setListening(false);
    setInterim('');
  }, []);

  const cleanupRecognition = useCallback(() => {
    clearSilenceTimer();
    const rec = recognitionRef.current;
    if (rec) {
      rec.onresult = null;
      rec.onerror = null;
      rec.onend = null;
      rec.onstart = null;
      rec.onaudiostart = null;
      rec.onspeechstart = null;
      rec.onaudioend = null;
      rec.onspeechend = null;
      try { rec.abort(); } catch { /* noop */ }
      recognitionRef.current = null;
    }
    setListening(false);
    setInterim('');
  }, [clearSilenceTimer]);

  /** HYBRID path — runs Chrome Web Speech for the live interim preview AND
   *  MediaRecorder in parallel for the authoritative Whisper transcript.
   *
   *  Why both: Chrome's Web Speech gives instant word-by-word streaming
   *  for the UI but is US-English-biased and mangles accents. Whisper
   *  handles UK regional accents materially better but doesn't stream.
   *  Hybrid = best of both: live preview + accent-robust final transcript.
   *
   *  On stop, Chrome's final transcript is DISCARDED and the captured
   *  audio is uploaded to /transcribe (Whisper). On Whisper failure we
   *  fall back to Chrome's text so the flow degrades gracefully.
   */
  const startNative = useCallback(async (Ctor: SpeechRecognitionCtor) => {
    setPhase('requesting');
    setStatus('starting microphone…');

    // Kick off MediaRecorder first — it's async (requires getUserMedia
    // permission grant). If it fails, we degrade to Chrome-only.
    let recorder: MediaRecorder | null = null;
    let mimeType: string | undefined;
    const hasMediaRecorder =
      typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
    if (hasMediaRecorder) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mimeType = pickMimeType();
        recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
        chunksRef.current = [];
        streamRef.current = stream;
        recorderRef.current = recorder;
        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) chunksRef.current.push(event.data);
        };
        recorder.start();
      } catch (err) {
        // getUserMedia denied or hardware missing — continue with Web Speech
        // only. The final transcript will come from Chrome instead of Whisper.
        console.warn('[voice] MediaRecorder setup failed, falling back to Chrome-only', err);
        recorder = null;
      }
    }

    try {
      const rec = new Ctor();
      rec.lang = typeof navigator !== 'undefined' ? navigator.language || 'en-GB' : 'en-GB';
      // Stream partial results so the UI can show words as they're heard.
      // `continuous: true` keeps the session alive across natural pauses;
      // we still end on stop() / onend.
      rec.interimResults = true;
      rec.continuous = true;
      finalTranscriptRef.current = '';
      setInterim('');

      rec.onstart = () => {
        setPhase('armed');
        setStatus('mic armed — start speaking');
      };
      rec.onaudiostart = () => {
        setPhase((p) => (p === 'hearing' ? p : 'listening'));
        setStatus('listening — say something');
      };
      rec.onspeechstart = () => {
        clearSilenceTimer();
        setPhase('hearing');
        setStatus(null);
      };
      rec.onspeechend = () => {
        armSilenceTimer();
      };
      rec.onresult = (event) => {
        let allFinal = '';
        let allInterim = '';
        for (let i = 0; i < event.results.length; i++) {
          const r = event.results[i];
          if (r.isFinal) allFinal += r[0].transcript;
          else allInterim += r[0].transcript;
        }
        finalTranscriptRef.current = allFinal.trim();
        const live = (allFinal + ' ' + allInterim).replace(/\s+/g, ' ').trim();
        setInterim(live);
        if (live) setPhase('hearing');
        if (finalTranscriptRef.current.length > 0 && allInterim.trim().length === 0) {
          armFinalResultTimer();
        }
      };
      rec.onerror = (event) => {
        const code = event.error;
        let msg: string;
        switch (code) {
          case 'not-allowed':
          case 'service-not-allowed':
            msg = 'Microphone blocked. Open chrome://settings/content/microphone and allow localhost:5180, then refresh.';
            setPermission('denied');
            break;
          case 'no-speech':
            msg = 'No speech detected. Speak closer to the mic, then click again.';
            break;
          case 'audio-capture':
            msg = 'No microphone detected on this device.';
            break;
          case 'network':
            msg = 'Speech service unreachable (network). Check internet, then retry.';
            break;
          case 'aborted':
            msg = '';
            break;
          default:
            msg = `Mic error: ${code}${event.message ? ` (${event.message})` : ''}`;
        }
        if (msg) setStatus(msg);
        setPhase('error');
        cleanupRecognition();
        // Also stop the recorder + release the mic stream.
        cleanup();
        busyRef.current = false;
      };
      rec.onend = () => {
        // Cache Chrome's final transcript as a fallback.
        const chromeText = finalTranscriptRef.current.trim();
        cleanupRecognition();

        // No recorder available — short-circuit on Chrome's text.
        if (!recorder) {
          busyRef.current = false;
          if (chromeText.length === 0) {
            setStatus('No speech captured — try again or type below.');
            setPhase('ended');
            return;
          }
          setStatus(null);
          setPhase('ended');
          onTranscript(normaliseSoccerTranscript(chromeText));
          return;
        }

        // Stop the recorder and route through Whisper. recorder.onstop
        // handles the upload + callback.
        setPhase('transcribing');
        setStatus('Transcribing…');
        recorder.onstop = () => {
          const type = recorder!.mimeType || mimeType || 'audio/webm';
          const blob = new Blob(chunksRef.current, { type });
          cleanup(); // stops stream tracks + resets refs
          busyRef.current = false;

          if (blob.size === 0) {
            // No audio captured — fall back to Chrome if we have anything.
            if (chromeText.length > 0) {
              setStatus(null);
              setPhase('ended');
              onTranscript(normaliseSoccerTranscript(chromeText));
            } else {
              setStatus('No speech captured — try again or type below.');
              setPhase('ended');
            }
            return;
          }

          transcribeAudio(blob)
            .then((whisperText) => {
              setStatus(null);
              setPhase('ended');
              onTranscript(normaliseSoccerTranscript(whisperText));
            })
            .catch((err: unknown) => {
              console.warn('[voice] Whisper failed, falling back to Chrome transcript', err);
              if (chromeText.length > 0) {
                setStatus('Used live transcript (Whisper unreachable).');
                setPhase('ended');
                onTranscript(normaliseSoccerTranscript(chromeText));
              } else {
                const msg = err instanceof Error ? err.message : String(err);
                setStatus(`Transcription failed: ${msg}`);
                setPhase('error');
              }
            });
        };
        // recorder.state can be 'inactive' if it already stopped (rare).
        if (recorder.state !== 'inactive') {
          try { recorder.stop(); } catch (err) {
            console.warn('[voice] recorder stop threw', err);
            // Force the onstop handler to run anyway by simulating it.
            recorder.onstop?.(new Event('stop') as Event);
          }
        } else {
          recorder.onstop?.(new Event('stop') as Event);
        }
      };

      recognitionRef.current = rec;
      rec.start();
      setListening(true);
    } catch (err) {
      cleanupRecognition();
      cleanup();
      busyRef.current = false;
      setPhase('error');
      setStatus(err instanceof Error ? err.message : String(err));
    }
  }, [cleanup, cleanupRecognition, onTranscript, armFinalResultTimer, armSilenceTimer, clearSilenceTimer]);

  const startUpload = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setStatus('Microphone is not available in this browser.');
      return;
    }
    if (typeof MediaRecorder === 'undefined') {
      setStatus('Recording is not available in this browser.');
      return;
    }

    busyRef.current = true;
    setStatus('Requesting microphone...');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      streamRef.current = stream;
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const type = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        cleanup();
        if (blob.size === 0) {
          setStatus('No audio captured.');
          busyRef.current = false;
          return;
        }
        setStatus('Transcribing...');
        transcribeAudio(blob)
          .then((text) => {
            setStatus(null);
            onTranscript(normaliseSoccerTranscript(text));
          })
          .catch((err: unknown) => {
            const msg = err instanceof Error ? err.message : String(err);
            if (/failed to fetch|networkerror/i.test(msg)) {
              setStatus(
                'mic upload offline — type below, or open this in Chrome/Edge for in-browser speech.',
              );
            } else {
              setStatus(msg);
            }
          })
          .finally(() => {
            busyRef.current = false;
          });
      };
      recorder.start();
      setListening(true);
      setStatus('Listening...');
    } catch (err) {
      cleanup();
      busyRef.current = false;
      setStatus(err instanceof Error ? err.message : String(err));
    }
  }, [cleanup, onTranscript]);

  const start = useCallback(async () => {
    if (busyRef.current || listening) return;
    busyRef.current = true;
    setPhase('requesting');
    // Prefer the hybrid path (Web Speech + MediaRecorder → Whisper).
    if (nativeCtor) {
      await startNative(nativeCtor);
      return;
    }
    // Browser doesn't expose Web Speech API — fall back to MediaRecorder
    // + Supabase /transcribe edge function (Whisper-only, no live preview).
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setPhase('unsupported');
      setStatus('Voice input is not supported in this browser. Use Chrome or Edge.');
      busyRef.current = false;
      return;
    }
    busyRef.current = false;
    await startUpload();
  }, [listening, nativeCtor, startNative, startUpload]);

  const stop = useCallback(() => {
    // Native path: ending recognition triggers onend which fires onTranscript.
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* noop */ }
      return;
    }
    // Upload path: stop the recorder, the onstop handler kicks off transcribe.
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    setListening(false);
    setStatus('Transcribing...');
    recorder.stop();
  }, []);

  // Make sure we don't leak a hot mic if the component unmounts mid-listen.
  useEffect(() => () => {
    cleanupRecognition();
    cleanup();
  }, [cleanup, cleanupRecognition]);

  return { listening, status, interim, phase, permission, start, stop };
}
